/**
 * Cobrado (Bioslab) × pago (operadora), lado a lado.
 *
 * Esta tela deliberadamente NÃO classifica a diferença como glosa, saldo ou
 * erro. Decisão do cliente: "pode ser um ajuste feito pelo faturamento antes de
 * enviar, ou uma glosa parcial, não dá pra afirmar". O sistema acumula a
 * evidência; a interpretação vem depois, com histórico.
 *
 * O agrupamento por razão é o que torna um deflator sistemático visível: na
 * amostra da Unimed, a razão foi exatamente 0,933333 (14/15) em todos os itens.
 *
 * E a razão sozinha não basta. O que decide se vale mexer no envio é a
 * **repetição no tempo**: a mesma razão voltando em fevereiro, março e abril,
 * na mesma operadora, não é ruído de um demonstrativo — é um padrão que
 * provavelmente nasce de como o faturamento monta o lote, e que dá para
 * corrigir na origem em vez de recorrer guia a guia todo mês. Por isso cada
 * razão abre em competência, operadora e arquivo, e a recorrente vem marcada.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { mensagemErro } from '../api/axios';
import { visaoDivergencias } from '../api/fluxfin';
import {
  Ajuda,
  Alerta,
  Badge,
  Botao,
  Busca,
  LoadingSpinner,
  PageHeader,
  Paginacao,
  Painel,
  Tabela,
  Th,
  Vazio,
} from '../components/common';
import { useOrdenacao } from '../hooks/useOrdenacao';
import { usePaginacao } from '../hooks/usePaginacao';
import {
  brl,
  erroCompetencia,
  mascararCompetencia,
  numero,
  percentual,
} from '../utils/formato';

export default function Divergencias() {
  const [itens, setItens] = useState([]);
  const [porRazao, setPorRazao] = useState([]);
  // `competencia` é o que está digitado; `competenciaAplicada` é o que vai ao
  // servidor. Separá-los conserta o defeito: antes, o primeiro dígito virava
  // `competencia=0`, que não casa com nada, e a tela esvaziava levando junto
  // os próprios filtros — que só eram renderizados quando havia itens.
  const [competencia, setCompetencia] = useState('');
  const [competenciaAplicada, setCompetenciaAplicada] = useState('');
  const [razaoSelecionada, setRazaoSelecionada] = useState(null);
  const [expandida, setExpandida] = useState(null);
  const [busca, setBusca] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    setCarregando(true);
    const filtros = {};
    if (competenciaAplicada) filtros.competencia = competenciaAplicada;
    if (razaoSelecionada !== null) filtros.razao = razaoSelecionada;
    visaoDivergencias(Object.keys(filtros).length ? filtros : undefined)
      .then((r) => {
        setItens(r.data.itens);
        // O resumo é sempre do conjunto inteiro: filtrar a lista não pode
        // apagar o panorama que faz a repetição no tempo aparecer.
        setPorRazao(r.data.resumo?.por_razao || []);
      })
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível carregar as divergências.')))
      .finally(() => setCarregando(false));
  }, [competenciaAplicada, razaoSelecionada]);

  const filtrados = useMemo(() => {
    const t = busca.trim().toLowerCase();
    if (!t) return itens;
    return itens.filter(
      (d) =>
        d.guia?.toLowerCase().includes(t) ||
        d.protocolo?.toLowerCase().includes(t) ||
        d.arquivo?.toLowerCase().includes(t) ||
        d.operadora?.toLowerCase().includes(t)
    );
  }, [itens, busca]);

  const ord = useOrdenacao(filtrados, 'diferenca', false);
  // Hook próprio para o resumo por razão: é outra lista, com outras colunas.
  const ordRazao = useOrdenacao(porRazao, 'diferenca_total', false);
  const pag = usePaginacao(ord.ordenados, 15);

  const totalDiferenca = filtrados.reduce((s, d) => s + Number(d.diferenca || 0), 0);

  return (
    <>
      <PageHeader
        titulo="Divergências cobrado × pago"
        subtitulo="O que o Bioslab registra como cobrado, ao lado do que a operadora pagou."
      />

      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>

      <div className="ff-aviso ff-aviso--info">
        <i className="bi bi-info-circle" aria-hidden="true" />
        <div>
          Esta tela <strong>não classifica</strong> a diferença. Ela pode ser ajuste do
          faturamento antes do envio, glosa parcial, deflator contratual ou tabela divergente —
          e ainda não há histórico para afirmar. Quando um percentual se repete de forma
          sistemática, ele aparece agrupado abaixo.
        </div>
      </div>

      {porRazao.length > 0 && (
        <div className="mb-4">
          <Painel
            titulo="Agrupado por razão"
            subtitulo="Razão = pago ÷ cobrado. Expanda para ver em que competências e arquivos ela apareceu."
            acoes={
              razaoSelecionada !== null && (
                <Botao
                  variante="secundario"
                  pequeno
                  icone="bi-x-lg"
                  onClick={() => setRazaoSelecionada(null)}
                >
                  Limpar filtro
                </Botao>
              )
            }
          >
            <Tabela estreita>
              <thead>
                <tr>
                  <Th campo="razao" estado={ordRazao}>
                    Razão
                    <Ajuda titulo="Razão" origem="valor pago ÷ valor cobrado">
                      Um percentual que se repete em muitas guias quase nunca é erro — é
                      regra. Frações simples (12/11, 4/3, 3/4) costumam indicar tabelas de
                      preço distintas entre os dois sistemas.
                    </Ajuda>
                  </Th>
                  <Th campo="qtd_guias" estado={ordRazao} alinhar="right">Guias</Th>
                  <Th campo="qtd_competencias" estado={ordRazao} alinhar="right">
                    Competências
                  </Th>
                  <Th campo="operadoras" estado={ordRazao}>Operadoras</Th>
                  <Th campo="diferenca_total" estado={ordRazao} alinhar="right">
                    Diferença acumulada
                  </Th>
                </tr>
              </thead>
              <tbody>
                {ordRazao.ordenados.map((r) => {
                  const chave = String(r.razao);
                  const aberta = expandida === chave;
                  const filtrando = razaoSelecionada === r.razao;
                  return (
                    <Fragment key={chave}>
                      <tr
                        className="ff-linha-clicavel"
                        onClick={() => setExpandida(aberta ? null : chave)}
                        style={filtrando ? { background: 'var(--ff-surface-2)' } : undefined}
                      >
                        <td className="col-codigo">
                          <i
                            className={`bi bi-chevron-${aberta ? 'down' : 'right'} me-2`}
                            aria-hidden="true"
                          />
                          {percentual(r.razao)}
                          {r.recorrente && (
                            <span className="ms-2">
                              <Badge tom="pendente">recorrente</Badge>
                            </span>
                          )}
                        </td>
                        <td className="col-qtd">{numero(r.qtd)}</td>
                        <td className="col-qtd">{numero(r.qtd_competencias)}</td>
                        <td className="col-meta">{(r.operadoras || []).join(', ') || '—'}</td>
                        <td className="col-valor" style={{ color: '#9a5b06' }}>
                          {brl(r.total_diferenca)}
                        </td>
                      </tr>

                      {aberta && (
                        <tr>
                          <td colSpan={5} style={{ background: 'var(--ff-surface-2)' }}>
                            <div className="p-3">
                              {r.recorrente && (
                                <div className="ff-aviso ff-aviso--atencao" style={{ margin: '0 0 12px' }}>
                                  <i className="bi bi-arrow-repeat" aria-hidden="true" />
                                  <div>
                                    Esta razão aparece em{' '}
                                    <strong>{r.qtd_competencias} competências</strong> (
                                    {r.competencias.join(', ')}). Repetição no tempo não é
                                    ruído de um demonstrativo — vale conferir como o envio
                                    é montado antes de recorrer guia a guia todo mês.
                                  </div>
                                </div>
                              )}

                              <Tabela estreita>
                                <thead>
                                  {/* sem-ordenacao: detalhamento de UMA razão,
                                      renderizado dentro do laço das razões —
                                      um hook por expansão é impossível, e um
                                      hook compartilhado ordenaria todas as
                                      expansões pelo mesmo campo. São poucas
                                      linhas por razão. */}
                                  <tr>
                                    <Th>Competência</Th>
                                    <Th>Operadora</Th>
                                    <Th>Arquivo</Th>
                                    <Th alinhar="right">Guias</Th>
                                    <Th alinhar="right">Diferença</Th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {r.detalhe.map((d, i) => (
                                    <tr key={`${chave}-${i}`}>
                                      <td className="col-meta">{d.competencia || '—'}</td>
                                      <td className="col-meta">{d.operadora || '—'}</td>
                                      <td className="col-codigo">{d.arquivo || '—'}</td>
                                      <td className="col-qtd">{numero(d.qtd)}</td>
                                      <td className="col-valor" style={{ color: '#9a5b06' }}>
                                        {brl(d.total_diferenca)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Tabela>

                              <div className="mt-3">
                                <Botao
                                  variante="secundario"
                                  pequeno
                                  onClick={() =>
                                    setRazaoSelecionada(filtrando ? null : r.razao)
                                  }
                                >
                                  {filtrando
                                    ? 'Parar de filtrar por esta razão'
                                    : 'Ver as guias desta razão'}
                                </Botao>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </Tabela>
          </Painel>
        </div>
      )}

      <Painel
        titulo="Guias com divergência"
        subtitulo={
          filtrados.length > 0
            ? `${numero(filtrados.length)} guia${filtrados.length === 1 ? '' : 's'} · ${brl(totalDiferenca)} de diferença`
            : undefined
        }
        busca={
          // Renderizado sempre que existe QUALQUER dado ou filtro ativo — nunca
          // some por causa de um filtro, o que deixaria o usuário sem como
          // desfazê-lo.
          (itens.length > 0 || competenciaAplicada || razaoSelecionada !== null) && (
            <>
              <Busca
                valor={busca}
                onChange={setBusca}
                placeholder="Buscar guia, protocolo, arquivo ou operadora"
                id="busca-divergencias"
              />
              <input
                className="ff-input"
                style={{ width: 170, maxWidth: '100%', height: 38 }}
                placeholder="Competência MM/AAAA"
                value={competencia}
                onChange={(e) => {
                  const v = mascararCompetencia(e.target.value);
                  setCompetencia(v);
                  // Só consulta com o valor inteiro, ou quando limpa. Um
                  // "0" ou "05" nunca casaria com "05/2026" — refazer a
                  // consulta a cada tecla só produziria tela vazia.
                  if (v === '' || (v.length === 7 && !erroCompetencia(v))) {
                    setCompetenciaAplicada(v);
                  }
                }}
                inputMode="numeric"
                maxLength={7}
                aria-label="Filtrar por competência"
              />
            </>
          )
        }
        rodape={filtrados.length > 0 && <Paginacao {...pag} rotulo="divergências" />}
      >
        {carregando ? (
          <LoadingSpinner />
        ) : itens.length === 0 ? (
          <Vazio
            icone="bi-arrow-left-right"
            titulo="Nenhuma divergência registrada"
            descricao="Divergências aparecem depois da conciliação, quando o valor cobrado no Bioslab difere do valor pago pela operadora."
          />
        ) : filtrados.length === 0 ? (
          <Vazio
            icone="bi-search"
            titulo="Nenhuma divergência corresponde ao filtro"
            descricao="Ajuste a busca, a competência ou a razão selecionada."
            acao={
              <Botao
                variante="secundario"
                onClick={() => {
                  setBusca('');
                  setCompetencia('');
                  setCompetenciaAplicada('');
                  setRazaoSelecionada(null);
                }}
              >
                Limpar filtros
              </Botao>
            }
          />
        ) : (
          <Tabela>
            <thead>
              <tr>
                <Th campo="guia" estado={ord}>
                  Guia
                </Th>
                <Th campo="protocolo" estado={ord}>
                  Protocolo
                </Th>
                <Th campo="valor_cobrado" estado={ord} alinhar="right">
                  Cobrado
                  <Ajuda titulo="Valor cobrado" origem="Exame_atendimento.vlr_contratante">
                    O que o Bioslab registra que foi cobrado nesta guia — a soma dos exames
                    do atendimento.
                  </Ajuda>
                </Th>
                <Th campo="valor_pago" estado={ord} alinhar="right">
                  Pago
                  <Ajuda
                    titulo="Valor pago"
                    origem="Arquivo da operadora · coluna Valor (R$)"
                  >
                    O que a operadora efetivamente pagou nesta guia, extraído do
                    demonstrativo e conferido contra o subtotal impresso no documento.
                  </Ajuda>
                </Th>
                <Th campo="valor_glosado" estado={ord} alinhar="right">
                  Glosado
                  <Ajuda titulo="Valor glosado" origem="Arquivo da operadora">
                    O que a operadora recusou nesta guia. Entra na conta da
                    diferença: boa parte do que parecia divergência é glosa já
                    explicada.
                    <br />
                    <br />
                    Célula vazia <strong>não é zero</strong> — significa que
                    nenhum arquivo falou de glosa para esta guia ainda.
                  </Ajuda>
                </Th>
                <Th campo="diferenca" estado={ord} alinhar="right">
                  Diferença
                </Th>
                <Th campo="razao" estado={ord} alinhar="right">
                  Razão
                  <Ajuda titulo="Razão" origem="valor pago ÷ valor cobrado">
                    Acima de 100% a operadora pagou mais que o cobrado; abaixo, pagou
                    menos. O sistema não classifica essa diferença.
                  </Ajuda>
                </Th>
                <Th campo="competencia" estado={ord}>
                  Competência
                  <Ajuda titulo="Competência" origem="Informada no upload · cabeçalho do demonstrativo">
                    Mês em que a operadora efetuou o pagamento. Não é o mês do atendimento —
                    um único pagamento costuma cobrir atendimentos de vários meses.
                  </Ajuda>
                </Th>
                <Th campo="arquivo" estado={ord}>
                  Arquivo
                </Th>
                <Th campo="operadora" estado={ord}>
                  Operadora
                </Th>
              </tr>
            </thead>
            <tbody>
              {pag.paginados.map((d, i) => (
                <tr key={`${d.guia}-${i}`}>
                  <td className="col-codigo">{d.guia}</td>
                  <td className="col-meta">{d.protocolo || '—'}</td>
                  <td className="col-qtd">{brl(d.valor_cobrado)}</td>
                  <td className="col-valor">{brl(d.valor_pago)}</td>
                  <td className="col-valor">
                    {/* Vazio nao e zero: a glosa pode nao ter chegado. */}
                    {d.valor_glosado === null || d.valor_glosado === undefined ? (
                      <span style={{ color: 'var(--ff-border-field)' }}>—</span>
                    ) : Number(d.valor_glosado) > 0 ? (
                      <span style={{ color: 'var(--ff-error-ink)' }}>
                        {brl(d.valor_glosado)}
                      </span>
                    ) : (
                      brl(d.valor_glosado)
                    )}
                  </td>
                  <td className="col-valor" style={{ color: '#9a5b06' }}>
                    {d.diferenca === null || d.diferenca === undefined ? (
                      <span
                        style={{ color: 'var(--ff-border-field)' }}
                        title="A glosa desta guia ainda não chegou — não dá para calcular."
                      >
                        —
                      </span>
                    ) : (
                      brl(d.diferenca)
                    )}
                  </td>
                  <td className="col-qtd">{percentual(d.razao)}</td>
                  <td className="col-meta">{d.competencia || '—'}</td>
                  <td className="col-codigo">{d.arquivo || '—'}</td>
                  <td className="col-meta">{d.operadora || '—'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan={4}>
                  Diferença total
                  {busca || competenciaAplicada || razaoSelecionada !== null ? ' (filtrada)' : ''}
                </th>
                <th className="col-valor">{brl(totalDiferenca)}</th>
                <th colSpan={4} />
              </tr>
            </tfoot>
          </Tabela>
        )}
      </Painel>
    </>
  );
}
